"use client";

import { useState } from "react";
import Link from "next/link";
import { Play } from "lucide-react";
import Screenshot from "../Screenshot";
import VideoModal from "../VideoModal";
import { track } from "../../../lib/analytics";
import { MARKETING_VIDEOS, isVideoReady } from "../../../lib/marketingVideos";

// Landing hero. Two-column layout: positioning + dual CTAs on the left,
// a real product screenshot on the right. Stacks on viewports below
// `lg`. Background is layered charcoal per founder direction (no solid
// forest-green page surfaces).
//
// The two first actions on the site (founder direction 2026-07-01):
// Start Free, or watch the demo. The secondary CTA opens the product
// demo in a modal when the clip is wired in lib/marketingVideos.ts;
// if the slot ever empties it degrades to the old fragment anchor into
// the how-it-works section.
export default function Hero() {
  const [demoOpen, setDemoOpen] = useState(false);
  const demo = MARKETING_VIDEOS.product_demo;
  const demoReady = isVideoReady(demo);

  function openDemo() {
    setDemoOpen(true);
    track({ name: "video_play", props: { which: demo.id } });
  }

  return (
    <section className="relative overflow-hidden bg-maren-charcoal text-maren-stone">
      <div className="max-w-7xl mx-auto px-6 pt-20 pb-16 sm:pt-24 sm:pb-20 lg:pt-28 lg:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-6">
            <p className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.25em] text-maren-gold">
              Maren · MCAT prep
            </p>

            {/* aria-label mirrors the visible copy so screen readers always
                get the full sentence even if JSX whitespace between the
                styled span and the preceding text node collapses.
                DRAFT copy pending the pre-launch wording review
                (docs/landing-copy-review.md). */}
            <h1
              aria-label="Turn every missed question into a clearer plan."
              className="mt-5 font-display font-medium tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl"
            >
              Turn every missed question into a{" "}
              <span className="text-maren-gold">clearer plan.</span>
            </h1>

            <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-maren-stone/75">
              Not another question bank. Maren sits on top of the practice you
              already do: the mistake log is free forever, and full access turns
              it into patterns, priorities, and what to study next.
            </p>

            {/* Primary action is the diagnostic, not signup. 86 of 132
                accounts have never logged a question, so asking for an
                email before the visitor has seen Maren do anything is the
                steepest drop in the funnel. The diagnostic proves the
                product first and asks second.

                The promise is deliberately "why you miss", not "your weak
                areas": the weak-area dashboard is the paid interpretation
                layer, and promising it here would be writing a cheque the
                free tier does not cash. */}
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                href="/diagnostic"
                onClick={() => track({ name: "cta_click", props: { location: "hero_primary_diagnostic" } })}
                className="inline-flex items-center justify-center rounded-lg bg-maren-green hover:bg-maren-green/90 active:bg-maren-green/80 px-6 py-3.5 text-sm font-semibold text-maren-stone shadow-sm hover:shadow-md transition focus:outline-none focus:ring-2 focus:ring-maren-gold/50 focus:ring-offset-2 focus:ring-offset-maren-charcoal"
              >
                Find out why you miss questions
              </Link>
              <Link
                href="/signup"
                onClick={() => track({ name: "cta_click", props: { location: "hero_secondary_signup" } })}
                className="inline-flex items-center justify-center rounded-lg border border-maren-sage/40 hover:border-maren-sage/70 hover:bg-maren-stone/5 px-6 py-3.5 text-sm font-semibold text-maren-stone transition focus:outline-none focus:ring-2 focus:ring-maren-gold/50 focus:ring-offset-2 focus:ring-offset-maren-charcoal"
              >
                Or start free
              </Link>
            </div>
            <p className="mt-3 text-sm text-maren-stone/60">
              5 questions, about 6 minutes. No account needed.
            </p>

            {/* The demo moved out of the first-action slot. It answers the
                same question the diagnostic now answers, but passively and
                about someone else. Kept reachable for visitors who would
                rather watch than do. */}
            {demoReady && (
              <button
                type="button"
                onClick={openDemo}
                className="mt-4 inline-flex items-center gap-2 text-sm text-maren-stone/70 hover:text-maren-stone underline underline-offset-4 transition focus:outline-none focus:ring-2 focus:ring-maren-gold/50 focus:ring-offset-2 focus:ring-offset-maren-charcoal rounded"
              >
                <Play className="h-3.5 w-3.5" aria-hidden />
                Watch the 90 second demo instead
              </button>
            )}

            {/* Named founder at the fold: research puts a named founder +
                credential in the top trust tier for this audience; an
                anonymous "built by a premed" line reads as costume. The
                photo chip reuses the founder portrait. */}
            <p className="mt-6 flex items-center gap-2.5 text-[13px] text-maren-stone/70 leading-relaxed">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/marketing/founder-isaiah.jpg"
                alt=""
                aria-hidden
                className="h-9 w-9 rounded-full object-cover object-top border border-maren-sage/40"
              />
              <span>
                Built by <span className="font-semibold text-maren-stone">Isaiah Phillips</span>.
                Premed, 496 to 512.
              </span>
            </p>
          </div>

          <div className="lg:col-span-6">
            {/* The founder's REAL dashboard (audit fix #2): same account
                the demo video shows, replacing the fictional demo-data
                screenshot. New filename (not overwriting the old PNG) so
                Vercel's long-lived image cache can't serve the stale
                render. */}
            <Screenshot
              src="/marketing/screen-dashboard-isaiah.png"
              alt="Isaiah's real Maren dashboard showing his top weak areas, streak, test-day countdown, and AAMC resource progress."
              width={1174}
              height={946}
              priority
              expandable
            />
          </div>
        </div>

        <ul className="mt-12 sm:mt-14 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-[12px] sm:text-xs text-maren-stone/65">
          <li>Works alongside the question bank you already use</li>
          <li aria-hidden className="text-maren-sage/40">·</li>
          <li>Syncs with Anki</li>
          <li aria-hidden className="text-maren-sage/40">·</li>
          <li>Free tracker, no card ever</li>
        </ul>
      </div>

      <VideoModal video={demo} open={demoOpen} onClose={() => setDemoOpen(false)} />
    </section>
  );
}
